import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Box, Button, Flex, FlexColumn, Grid, Heading, LinkButton, Text } from 'core/components';
import { getTestTypeLabel } from 'app/views/synthetics/utils/syntheticsUtils';
import { getMetricsForTest } from '@kentik/ui-shared/synthetics/healthMetricMapping';
import { getBgpVpThreshold } from '@kentik/ui-shared/synthetics/formatThreshold';
import LabelValue from 'app/views/metrics/components/LabelValue';
import KentikAgentDrawerSection from 'app/views/settings/kentikAgent/agentDrawer/KentikAgentDrawerSection';
import {
  HealthThresholdsList,
  GroupedAlertingSummary,
  AlertWindowSummary
} from 'app/views/synthetics/components/TestConfigSections';

const LABEL_WIDTH = 110;

@inject('$lookups')
@observer
class TestConfigDetails extends Component {
  state = {
    asnLabelMap: {}
  };

  componentDidMount() {
    this.loadAsnLabels();
  }

  componentDidUpdate(prevProps) {
    const { test } = this.props;
    const { test: prevTest } = prevProps;
    if (this.getAsnLookupKey(prevTest) !== this.getAsnLookupKey(test)) {
      this.loadAsnLabels();
    }
  }

  getNormalizedAsnValues(testArg) {
    const { test } = this.props;
    const lookupTest = testArg || test;
    const bgp = lookupTest?.get('config.bgp') || {};
    const originValues = this.parseCommaSeparatedValues(bgp.origin);
    const upstreamValues = this.parseCommaSeparatedValues(bgp.upstream);

    return [...new Set([...originValues, ...upstreamValues].filter((value) => /^\d+$/.test(value)))].sort(
      (left, right) => Number(left) - Number(right)
    );
  }

  getAsnLookupKey(testArg) {
    return this.getNormalizedAsnValues(testArg).join('|');
  }

  loadAsnLabels = () => {
    const { $lookups, test } = this.props;
    if (!$lookups || !test) {
      return Promise.resolve();
    }

    const asnValues = this.getNormalizedAsnValues(test);
    const promiseKey = `synthetics-asData:${this.getAsnLookupKey(test)}`;

    if (!asnValues.length) {
      this.setState({ asnLabelMap: {} });
      return Promise.resolve();
    }

    this.lookupRequestId = (this.lookupRequestId || 0) + 1;
    const requestId = this.lookupRequestId;

    return $lookups
      .asData(asnValues, { debounce: false, promiseKey })
      .then((results) => {
        if (requestId !== this.lookupRequestId) {
          return;
        }

        const asnLabelMap = (results || []).reduce((acc, option) => {
          const value = `${option.value}`;
          const description = typeof option.label === 'string' ? option.label.trim() : '';
          acc[value] = description ? `${value} (${description})` : value;
          return acc;
        }, {});

        this.setState({ asnLabelMap });
      })
      .catch(() => {
        if (requestId === this.lookupRequestId) {
          this.setState({ asnLabelMap: {} });
        }
      });
  };

  formatAsnValue = (value) => {
    const { asnLabelMap } = this.state;
    const normalized = `${value}`.trim();
    return asnLabelMap[normalized] || normalized;
  };

  renderLabelValues(dataMap) {
    return dataMap
      .filter(({ value }) => value != null && value !== '' && value !== '—' && value !== 0)
      .map(({ label, value }) => (
        <LabelValue key={label} label={label} value={value} labelWidth={LABEL_WIDTH} mb={0} alignItems="center" />
      ));
  }

  parseCommaSeparatedValues(value) {
    if (Array.isArray(value)) {
      return value
        .flatMap((item) => (typeof item === 'string' ? item.split(',') : [item]))
        .map((item) => `${item}`.trim())
        .filter(Boolean);
    }

    if (typeof value === 'number') {
      return [`${value}`];
    }

    if (typeof value !== 'string') {
      return [];
    }

    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  formatProtocol(value, fallback = null) {
    const protocol = value || fallback;
    return typeof protocol === 'string' ? protocol.toUpperCase() : protocol;
  }

  uniqueValues(values) {
    return [...new Set(values)];
  }

  renderCollapsibleSection(title, content, props = {}) {
    if (!content) {
      return null;
    }

    return (
      <KentikAgentDrawerSection title={title} openOnMount={false} {...props}>
        {content}
      </KentikAgentDrawerSection>
    );
  }

  get testInfoFields() {
    const { test } = this.props;
    const testType = test.get('test_type');
    const status = test.isPaused ? 'Paused' : 'Active';
    const isBgpMonitor = test.isBgp;

    const fields = [
      { label: 'Test Type', value: getTestTypeLabel(testType) },
      { label: 'Status', value: status }
    ];

    if (!isBgpMonitor) {
      fields.push({ label: 'Frequency', value: test.frequencyLabel });
      fields.push({ label: 'IP Family', value: test.get('config.family') || 'v4' });
    }

    if (isBgpMonitor) {
      const bgp = test.get('config.bgp') || {};
      const prefixes = this.uniqueValues(this.parseCommaSeparatedValues(bgp.prefix));
      const prefixValue =
        prefixes.length > 1 ? (
          <FlexColumn>
            {prefixes.map((prefix) => (
              <Text key={prefix}>{prefix}</Text>
            ))}
          </FlexColumn>
        ) : (
          prefixes[0] || bgp.prefix
        );
      fields.push({ label: prefixes.length > 1 ? 'Prefixes' : 'Prefix', value: prefixValue });
      fields.push({ label: 'Include Covered', value: bgp.include_covered ? 'Yes' : 'No' });
    }

    if (test.isDns) {
      const serversRaw = test.get('config.servers');
      const servers =
        typeof serversRaw === 'string'
          ? serversRaw
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [];
      if (servers.length > 0) {
        const target = test.get('config.target.value');
        fields.push({ label: 'Host', value: target });
        fields.push({
          label: 'DNS Resolvers',
          value: (
            <FlexColumn>
              {servers.map((server) => (
                <Text key={server}>{server}</Text>
              ))}
            </FlexColumn>
          )
        });
      }
    } else if (!isBgpMonitor) {
      const target = test.get('config.target.value');
      if (target) {
        fields.push({ label: 'Target', value: target });
      }
    }

    return fields;
  }

  get pingConfig() {
    const { test } = this.props;
    const tasks = test.get('config.tasks') || [];
    if (!tasks.includes('ping')) {
      return null;
    }

    const config = test.get('config.ping') || {};
    return this.renderLabelValues([
      { label: 'Protocol', value: this.formatProtocol(config.protocol || test.get('config.protocol'), 'ICMP') },
      { label: 'Port', value: config.port },
      { label: 'Count', value: config.count },
      { label: 'Timeout', value: config.expiry ? `${config.expiry}ms` : null },
      { label: 'Delay', value: config.delay ? `${config.delay}ms` : null }
    ]);
  }

  get traceConfig() {
    const { test } = this.props;
    const tasks = test.get('config.tasks') || [];
    if (!tasks.includes('traceroute')) {
      return null;
    }

    const config = test.get('config.trace') || {};
    const rawProtocol = config.protocol || 'icmp';
    const protocol = this.formatProtocol(rawProtocol);
    const isIcmp = typeof rawProtocol === 'string' && rawProtocol.toLowerCase() === 'icmp';
    return this.renderLabelValues([
      { label: 'Protocol', value: protocol },
      { label: 'Max Hops', value: config.limit },
      { label: 'Port', value: isIcmp ? null : config.port },
      { label: 'Timeout', value: config.expiry ? `${config.expiry}ms` : null }
    ]);
  }

  get httpConfig() {
    const { test } = this.props;
    const tasks = test.get('config.tasks') || [];
    const hasHttp = tasks.includes('http') || tasks.includes('page-load') || tasks.includes('transaction');
    if (!hasHttp) {
      return null;
    }

    const config = test.get('config.http') || {};
    const expiry = config.expiry || test.get('config.expiry');
    return this.renderLabelValues([
      { label: 'Method', value: config.method || 'GET' },
      { label: 'Timeout', value: expiry ? `${expiry}ms` : null },
      { label: 'Ignore TLS', value: config.ignore_tls_errors ? 'Yes' : 'No' }
    ]);
  }

  get dnsConfig() {
    const { test } = this.props;
    if (!test.isDns) {
      return null;
    }

    const recordType = test.get('config.type');
    const port = test.get('config.port');
    const period = test.get('config.period');
    return this.renderLabelValues([
      { label: 'Record Type', value: recordType },
      { label: 'Port', value: port },
      { label: 'Timeout', value: period ? `${period * 1000}ms` : null }
    ]);
  }

  get bgpConfig() {
    const { test } = this.props;
    const tasks = test.get('config.tasks') || [];
    const isBgpMonitor = test.isBgp;

    if (!tasks.includes('bgp-monitor') && !isBgpMonitor) {
      return null;
    }

    // For BGP monitor tests, show health-oriented layout
    if (isBgpMonitor) {
      return this.bgpHealthContent;
    }

    // For non-BGP-monitor tests with BGP task (e.g. HTTP tests), keep existing display
    const bgp = test.get('config.bgp') || {};
    const prefixes = this.uniqueValues(this.parseCommaSeparatedValues(bgp.prefix));
    const origins = this.uniqueValues(this.parseCommaSeparatedValues(bgp.origin));
    const upstreams = this.uniqueValues(this.parseCommaSeparatedValues(bgp.upstream));

    const prefixValue =
      prefixes.length > 1 ? (
        <FlexColumn>
          {prefixes.map((prefix) => (
            <Text key={prefix}>{prefix}</Text>
          ))}
        </FlexColumn>
      ) : (
        prefixes[0] || bgp.prefix
      );

    const originValue =
      origins.length > 1 ? (
        <FlexColumn>
          {origins.map((origin) => (
            <Text key={origin}>{this.formatAsnValue(origin)}</Text>
          ))}
        </FlexColumn>
      ) : (
        (origins[0] && this.formatAsnValue(origins[0])) || bgp.origin
      );

    const upstreamValue =
      upstreams.length > 1 ? (
        <FlexColumn>
          {upstreams.map((upstream) => (
            <Text key={upstream}>{this.formatAsnValue(upstream)}</Text>
          ))}
        </FlexColumn>
      ) : (
        (upstreams[0] && this.formatAsnValue(upstreams[0])) || 'Any'
      );

    return this.renderLabelValues([
      { label: prefixes.length > 1 ? 'Prefixes' : 'Prefix', value: prefixValue },
      { label: origins.length > 1 ? 'Origin ASNs' : 'Origin ASN', value: originValue },
      { label: upstreams.length > 1 ? 'Upstream ASNs' : 'Upstream ASN', value: upstreamValue },
      { label: 'Check RPKI', value: bgp.check_rpki ? 'Yes' : 'No' },
      { label: 'Include Covered', value: bgp.include_covered ? 'Yes' : 'No' }
    ]);
  }

  renderVpThreshold(threshold, { inverted = false, heading } = {}) {
    if (!threshold) {
      return null;
    }

    const symbol = inverted ? '\u2264' : '\u2265';

    const grid = (
      <Grid gridTemplateColumns="1fr 1fr" gridGap="4px 8px" ml={1} mt="4px">
        {threshold.warningDisplay && (
          <>
            <Flex alignItems="center" gap="4px">
              <Box width={8} height={8} borderRadius="50%" bg="warning" flexShrink={0} />
              <Text muted>Warning</Text>
            </Flex>
            <Text>
              {symbol} {threshold.warningDisplay}
            </Text>
          </>
        )}
        {threshold.criticalDisplay && (
          <>
            <Flex alignItems="center" gap="4px">
              <Box width={8} height={8} borderRadius="50%" bg="danger" flexShrink={0} />
              <Text muted>Critical</Text>
            </Flex>
            <Text>
              {symbol} {threshold.criticalDisplay}
            </Text>
          </>
        )}
      </Grid>
    );

    if (!heading) {
      return grid;
    }

    return (
      <Box mt="4px">
        <Text as="div" fontWeight="bold" fontStyle="italic" muted fontSize={15}>
          {heading}
        </Text>
        {grid}
      </Box>
    );
  }

  get bgpHealthContent() {
    const { test } = this.props;
    const bgp = test.get('config.bgp') || {};
    const healthSettings = test.get('config.healthSettings') || {};
    const disabledMetrics = healthSettings.disabledMetrics || {};

    const origins = this.uniqueValues(this.parseCommaSeparatedValues(bgp.origin));
    const upstreams = this.uniqueValues(this.parseCommaSeparatedValues(bgp.upstream));
    const prefixes = this.uniqueValues(this.parseCommaSeparatedValues(bgp.prefix));

    const isReachabilityDisabled = disabledMetrics.bgpReachability === true || prefixes.length === 0;
    const isOriginHijackDisabled =
      disabledMetrics.bgpOriginHijack === true || (origins.length === 0 && !bgp.check_rpki);
    const isUpstreamLeakDisabled = disabledMetrics.bgpUpstreamLeak === true || upstreams.length === 0;

    // Fall back to the wizard/form defaults so legacy tests (no nested VP objects) still show effective thresholds.
    const reachabilityThreshold = getBgpVpThreshold(healthSettings.reachability, {
      warningPct: healthSettings.reachabilityWarning ?? 80,
      criticalPct: healthSettings.reachabilityCritical ?? 50
    });
    const originHijackThreshold = getBgpVpThreshold(healthSettings.originHijack, { warningPct: 5, criticalPct: 50 });
    const upstreamLeakThreshold = getBgpVpThreshold(healthSettings.upstreamLeak, { warningPct: 5, criticalPct: 50 });
    const upstreamLeak = healthSettings.upstreamLeak || {};

    return (
      <FlexColumn gap="28px">
        <Box opacity={isReachabilityDisabled ? 0.4 : 1}>
          <Flex as="div" alignItems="center" fontWeight="bold" mb="8px">
            Reachability Thresholds
            {isReachabilityDisabled && (
              <Text muted fontWeight="normal">
                {' '}
                (disabled)
              </Text>
            )}
          </Flex>
          <Box ml={1}>{this.renderVpThreshold(reachabilityThreshold, { inverted: true })}</Box>
        </Box>

        <Box opacity={isOriginHijackDisabled ? 0.4 : 1}>
          <Text as="div" fontWeight="bold" mb="8px">
            Origin Hijack Detection
            {isOriginHijackDisabled && (
              <Text muted fontWeight="normal">
                {' '}
                (disabled)
              </Text>
            )}
          </Text>
          <FlexColumn ml={1} gap="8px">
            {this.renderLabelValues([
              {
                label: origins.length > 1 ? 'Origin ASNs' : 'Origin ASN',
                value:
                  origins.length > 1 ? (
                    <FlexColumn gap="4px">
                      {origins.map((origin) => (
                        <Text key={origin}>{this.formatAsnValue(origin)}</Text>
                      ))}
                    </FlexColumn>
                  ) : (
                    (origins[0] && this.formatAsnValue(origins[0])) || '—'
                  )
              },
              { label: 'Check RPKI', value: bgp.check_rpki ? 'Yes' : 'No' }
            ])}
            {this.renderVpThreshold(originHijackThreshold, { heading: 'VP Threshold' })}
          </FlexColumn>
        </Box>

        <Box opacity={isUpstreamLeakDisabled ? 0.4 : 1}>
          <Text as="div" fontWeight="bold" mb="8px">
            Upstream Leak Detection
            {isUpstreamLeakDisabled && (
              <Text muted fontWeight="normal">
                {' '}
                (disabled)
              </Text>
            )}
          </Text>
          <FlexColumn ml={1} gap="8px">
            {upstreams.length > 0 &&
              this.renderLabelValues([
                {
                  label: upstreams.length > 1 ? 'Upstream ASNs' : 'Upstream ASN',
                  value:
                    upstreams.length > 1 ? (
                      <FlexColumn gap="4px">
                        {upstreams.map((upstream) => (
                          <Text key={upstream}>{this.formatAsnValue(upstream)}</Text>
                        ))}
                      </FlexColumn>
                    ) : (
                      this.formatAsnValue(upstreams[0])
                    )
                }
              ])}
            {this.renderVpThreshold(upstreamLeakThreshold, { heading: 'VP Threshold' })}
            {this.renderLabelValues([
              {
                label: 'Automatically Detect Peers',
                value: upstreamLeak.automaticallyDetectPeers === false ? 'No' : 'Yes'
              },
              { label: 'Detect Misplaced Upstream', value: upstreamLeak.detectMisplacedUpstream ? 'Yes' : 'No' }
            ])}
          </FlexColumn>
        </Box>
      </FlexColumn>
    );
  }

  get healthThresholdsContent() {
    const { test } = this.props;
    if (test.isBgp) {
      return null;
    }

    const healthSettings = test.get('config.healthSettings');
    const tasks = test.get('config.tasks') || [];
    const testType = test.get('test_type');

    if (!healthSettings || healthSettings.healthDisabled) {
      return null;
    }

    const metricDescriptors = getMetricsForTest(tasks, testType);
    if (!metricDescriptors.length) {
      return null;
    }

    return <HealthThresholdsList healthSettings={healthSettings} tasks={tasks} testType={testType} />;
  }

  get groupedAlertingContent() {
    const { test } = this.props;
    const alerting = test.get('config.alerting') || {};

    if (alerting.alertingType !== 'grouped') {
      return null;
    }

    return <GroupedAlertingSummary alerting={alerting} />;
  }

  get alertWindowContent() {
    const { test } = this.props;
    if (test.isBgp) {
      return null;
    }

    const activate = test.get('config.activate') || {};

    if (!activate.times && !activate.timeWindow && !activate.gracePeriod) {
      return null;
    }

    return <AlertWindowSummary activate={activate} />;
  }

  render() {
    const { test, editUrl, onClose } = this.props;

    return (
      <Flex flexDirection="column" height="100%" overflow="auto">
        <Flex alignItems="center" pt={2} px="28px" justifyContent="space-between">
          <Button onClick={onClose} minWidth={70} text="Close" />
          {editUrl && <LinkButton text="Edit Test" to={editUrl} minWidth={70} />}
        </Flex>
        <FlexColumn alignItems="center" gap="4px" px="28px" pb={3} borderBottom="thin" mb={1}>
          <Text muted>Test Config</Text>
          <Heading level={4} fontWeight="heavy" m={0} textAlign="center">
            {test.display_name}
          </Heading>
          <Text muted fontStyle="italic" fontSize="11px">
            Based on current test and health settings
          </Text>
        </FlexColumn>

        <FlexColumn gap="12px" px="28px" borderBottom="thin" pb={3}>
          {this.renderLabelValues(this.testInfoFields)}
        </FlexColumn>

        {this.renderCollapsibleSection('Ping Config', this.pingConfig)}
        {this.renderCollapsibleSection('Trace Config', this.traceConfig)}
        {this.renderCollapsibleSection('HTTP Config', this.httpConfig)}
        {this.renderCollapsibleSection('DNS Config', this.dnsConfig)}
        {this.renderCollapsibleSection(test.isBgp ? 'Health Settings' : 'BGP Config', this.bgpConfig)}
        {this.renderCollapsibleSection('Health Thresholds', this.healthThresholdsContent)}
        {this.renderCollapsibleSection('Grouped Alerting', this.groupedAlertingContent)}
        {this.renderCollapsibleSection('Alert Window', this.alertWindowContent)}
      </Flex>
    );
  }
}

export default TestConfigDetails;
